import { type FC, useState, useCallback } from "react";
import Modal from "@root/shared/components/Modal";
import { Snackbar } from "@root/shared/components/Snackbar";
import { LazyLoadImage } from "@root/shared/components/LazyLoadImage";
import { copyToClipboard } from "@root/shared/utils/copyToClipboard";
import { PcContent } from "./uiParts/PcContent";
import { SpContent } from "./uiParts/SpContent";
import * as styles from "./styles.css";

// NOTE: QRコード経由の遷移時にモーダルを自動表示するためのパラメータ
const MODAL_PARAM_KEY = "modal";
const MODAL_PARAM_VALUE = "instagram_share";

const shouldAutoOpen = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get(MODAL_PARAM_KEY) === MODAL_PARAM_VALUE;
};

type Props = {
  shareUrl: string;
  displayImageUrl: string;
  shareImageUrl: string;
  isSp: boolean;
};

export const InstagramShareModal: FC<Props> = ({
  shareUrl,
  displayImageUrl,
  shareImageUrl,
  isSp,
}) => {
  const [isShowModal, setIsShowModal] = useState(shouldAutoOpen);
  const [isCopied, setIsCopied] = useState(false);

  // NOTE: URLをクリップボードにコピーし、成功時にSnackbarを表示する
  const handleCopyUrl = useCallback(async () => {
    const success = await copyToClipboard(shareUrl);
    if (success) setIsCopied(true);
  }, [shareUrl]);

  // NOTE: 画像をfetchしてWeb Share APIでInstagramアプリに共有する
  const handleShareInstagram = useCallback(async () => {
    try {
      const response = await fetch(shareImageUrl);
      if (!response.ok) return;
      const blob = await response.blob();
      const file = new File([blob], "awards.jpg", { type: blob.type });

      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file] });
      }
    } catch {
      // ユーザーがシェアをキャンセルした場合（AbortError）等は無視
    }
  }, [shareImageUrl]);

  // NOTE: QRコード用URLにパラメータを付与
  const qrCodeUrl = (() => {
    const url = new URL(shareUrl, window.location.origin);
    url.searchParams.set(MODAL_PARAM_KEY, MODAL_PARAM_VALUE);
    return url.toString();
  })();

  return (
    <>
      <button type="button" className={styles.triggerButton} onClick={() => setIsShowModal(true)}>
        <div className={styles.triggerIconWrapper}>
          <LazyLoadImage
            src="/images/user_frontend/special/rejob_awards_2026/icn_sns_instagram.png"
            alt="Instagramでシェア"
            width={40}
            height={40}
          />
        </div>
      </button>

      <Modal
        isShow={isShowModal}
        pcWidthType="small"
        spAppearanceType="center"
        headerTitleStyle="none"
        onClose={() => setIsShowModal(false)}
        {
          // NOTE: PCでもfooterContent・footerContentStyleを渡すとフッターに表示されてしまうため、SP時のみ渡す
          ...(isSp && {
            footerContent: (
              <button
                type="button"
                className={styles.spInstagramShareButton}
                onClick={handleShareInstagram}
              >
                Instagramでシェアする
              </button>
            ),
            footerContentStyle: "button",
          })
        }
      >
        <div>
          <PcContent qrCodeUrl={qrCodeUrl} />
          <SpContent displayImageUrl={displayImageUrl} onCopyUrl={handleCopyUrl} />
        </div>
      </Modal>

      <Snackbar
        isOpened={isCopied}
        message={<div className={styles.snackbarMessage}>コピーしました。</div>}
        onClose={() => setIsCopied(false)}
      />
    </>
  );
};
